import React, { useLayoutEffect, useRef, useState } from "react";

const PEEK_GAP = 4;
const PEEK_EDGE = 8;
const PEEK_FALLBACK_WIDTH = 320;
const PEEK_FALLBACK_HEIGHT = 180;

/**
 * Resolve a viewport rect from a click event or DOM element.
 * @param {MouseEvent|React.SyntheticEvent|Element|null|undefined} source
 * @returns {{ top: number, left: number, bottom: number, right: number, width: number, height: number }|null}
 */
export function getPeekAnchorRect(source) {
  if (!source) return null;
  const el =
    source.currentTarget && typeof source.currentTarget.getBoundingClientRect === "function"
      ? source.currentTarget
      : source.target && typeof source.target.closest === "function"
        ? source.target.closest("button, a, [data-peek-anchor]") || source.target
        : typeof source.getBoundingClientRect === "function"
          ? source
          : null;
  if (el && typeof el.getBoundingClientRect === "function") {
    const r = el.getBoundingClientRect();
    return {
      top: r.top,
      left: r.left,
      bottom: r.bottom,
      right: r.right,
      width: r.width,
      height: r.height,
    };
  }
  if (typeof source.clientX === "number" && typeof source.clientY === "number") {
    return {
      top: source.clientY,
      left: source.clientX,
      bottom: source.clientY,
      right: source.clientX,
      width: 0,
      height: 0,
    };
  }
  return null;
}

function clampPeekPosition(anchor, panelW, panelH) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = anchor ? anchor.left : (vw - panelW) / 2;
  let top = anchor ? anchor.bottom + PEEK_GAP : (vh - panelH) / 2;

  if (left + panelW > vw - PEEK_EDGE) {
    left = Math.max(PEEK_EDGE, vw - panelW - PEEK_EDGE);
  }
  left = Math.max(PEEK_EDGE, left);

  if (top + panelH > vh - PEEK_EDGE) {
    const above = (anchor ? anchor.top : top) - panelH - PEEK_GAP;
    top = above >= PEEK_EDGE ? above : Math.max(PEEK_EDGE, vh - panelH - PEEK_EDGE);
  }
  top = Math.max(PEEK_EDGE, top);

  return { left, top };
}

/**
 * Stacked overlay that presents a platform entity list card near an anchor (click target).
 * Wrap content in `cards-wrapper` so card CSS variables and accent bar apply.
 *
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   stacked?: boolean,
 *   anchor?: { top: number, left: number, bottom: number, right: number, width?: number, height?: number }|null,
 *   children?: React.ReactNode,
 * }} props
 */
export default function EntityCardPeekOverlay({
  open,
  onClose,
  stacked = true,
  anchor = null,
  children,
}) {
  const cardRef = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }

    const update = () => {
      const panel = cardRef.current;
      const panelW = panel?.offsetWidth || PEEK_FALLBACK_WIDTH;
      const panelH = panel?.offsetHeight || PEEK_FALLBACK_HEIGHT;
      setPos(clampPeekPosition(anchor, panelW, panelH));
    };

    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, anchor]);

  if (!open) return null;

  return (
    <div
      className={`detail-modal-overlay detail-modal-overlay--peek${
        stacked ? " detail-modal-overlay--stacked" : ""
      }`}
    >
      <div
        role="button"
        tabIndex={0}
        className="detail-modal-backdrop detail-modal-backdrop--peek"
        onClick={onClose}
        onKeyDown={(e) => e.key === "Escape" && onClose()}
        aria-label="Close"
      />
      <div
        ref={cardRef}
        className="cards-wrapper entity-card-peek"
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed",
          left: pos ? pos.left : -9999,
          top: pos ? pos.top : 0,
          visibility: pos ? "visible" : "hidden",
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
